/**
 * SSR-compatible ContactAgentModal
 *
 * Same UI/UX as components/ContactAgentModal.tsx but does NOT depend on
 * AuthContext (useAuth). User data is passed in via props so it works
 * inside the Vike SSR pages that don't wrap with AuthProvider.
 *
 * CHAT-02a: flag-gated repoint of the contact CTA onto the conversations model.
 * Navigation here uses window.location (no react-router) to stay SSR-safe.
 */

import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle, Loader2, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { createLead } from '../../lib/api/leadsApi';
import { isChatCtaEnabled } from '../lib/platformSettings';
import { findOrCreateConversation } from '../lib/conversations';

interface UserInfo {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface ContactAgentModalSSRProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: string;
  agentId: string;
  agentName: string;
  propertyTitle: string;
  user?: UserInfo | null;
}

const ContactAgentModalSSR: React.FC<ContactAgentModalSSRProps> = ({
  isOpen,
  onClose,
  listingId,
  agentId,
  agentName,
  propertyTitle,
  user,
}) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    message: `Hi, I'm interested in this property: ${propertyTitle}`,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CHAT-02a: true while the conversations CTA is taking over (flag ON).
  const [chatRedirecting, setChatRedirecting] = useState(false);

  // Update form data when user info changes (e.g. logs in while modal is open)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || '',
      }));
    }
  }, [user]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        message: `Hi, I'm interested in this property: ${propertyTitle}`,
      });
      setError(null);
      setIsSuccess(false);
      setChatRedirecting(false);
    }
  }, [isOpen, propertyTitle, user?.name, user?.email, user?.phone]);

  // CHAT-02a: when the conversations CTA flag is ON, repoint contact away from
  // the leads form. Anonymous → /auth (no auto-resume); logged-in → find-or-
  // create the conversation and open its thread. Flag defaults OFF, so this is
  // a no-op and the existing leads behavior below is completely unchanged.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      const enabled = await isChatCtaEnabled();
      if (cancelled || !enabled) return;

      setChatRedirecting(true);

      if (!user?.id) {
        // Use the redirect mechanism Auth.tsx actually reads (localStorage),
        // not a query param: a ?redirect= is dropped during Google OAuth (the
        // provider redirects to /), whereas localStorage survives the round-trip
        // so the buyer lands back on this PDP after signing in.
        localStorage.setItem('redirect_after_auth', window.location.pathname);
        window.location.href = '/auth';
        return;
      }

      try {
        const conversationId = await findOrCreateConversation({ listingId, agentId });
        if (cancelled) return;
        window.location.href = `/messages/${conversationId}`;
      } catch (err: unknown) {
        if (cancelled) return;
        setChatRedirecting(false);
        setError(err instanceof Error ? err.message : 'Could not open the conversation. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      setError('Please provide email or phone number');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { success, error: leadError } = await createLead({
        listingId,
        agentId,
        buyerId: user?.id,
        buyerName: formData.name,
        buyerEmail: formData.email,
        buyerPhone: formData.phone,
        message: formData.message,
      });

      if (!success) {
        throw new Error(leadError || 'Failed to send message');
      }

      setIsSuccess(true);

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData(prev => ({ ...prev, message: '' }));
      }, 2000);
    } catch (err: unknown) {
      console.error('Contact error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // CHAT-02a: conversations CTA is taking over — show a brief loading state
  // instead of the leads form while we redirect to the thread / auth.
  if (chatRedirecting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl">
          <Loader2 className="animate-spin text-green-700 mx-auto mb-4" size={32} />
          <p className="text-gray-500">Opening conversation…</p>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-500" size={32} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
          <p className="text-gray-500">
            {agentName} will get back to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Contact Agent</h3>
            <p className="text-white/80 text-sm">{agentName}</p>
          </div>
          <button
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="text-white" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Your Name *
            </label>
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-600/20 text-gray-900 placeholder-gray-500"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-600/20 text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+254 700 000 000"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-600/20 text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Message
            </label>
            <div className="relative">
              <MessageSquare size={18} className="absolute left-4 top-4 text-gray-400" />
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="I'm interested in this property..."
                rows={3}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-600/20 resize-none text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Message
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500">
            The agent will receive your contact details and message
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactAgentModalSSR;
